/**
 * Freehand Drawing Tool - Snapping System
 * Advanced snapping with grid, angles, objects, and visual feedback
 */

import { vec2, snapAngle, snapToOrthogonal, snapToGrid, getBoundingBox } from './geometry';

// ============================================================================
// Snap Target Types
// ============================================================================

export class SnappingEngine {
  constructor(config, bounds) {
    this.config = config;
    this.bounds = bounds;
    this.objectTargets = [];
    this.guideTargets = [];
  }

  updateConfig(config) {
    this.config = config;
  }

  updateBounds(bounds) {
    this.bounds = bounds;
  }

  addPaths(paths) {
    this.objectTargets = [];
    
    paths.forEach(path => {
      if (!path.visible || path.locked) return;

      // Add endpoint targets
      path.nodes.forEach((node, index) => {
        this.objectTargets.push({
          id: `${path.id}_node_${index}`,
          type: 'endpoint',
          point: node.p,
          priority: 10,
          metadata: { pathId: path.id, nodeIndex: index }
        });

        // Add handle targets if visible
        if (node.h1) {
          this.objectTargets.push({
            id: `${path.id}_handle1_${index}`,
            type: 'endpoint',
            point: node.h1,
            priority: 5,
            metadata: { pathId: path.id, nodeIndex: index, handle: 'h1' }
          });
        }

        if (node.h2) {
          this.objectTargets.push({
            id: `${path.id}_handle2_${index}`,
            type: 'endpoint',
            point: node.h2,
            priority: 5,
            metadata: { pathId: path.id, nodeIndex: index, handle: 'h2' }
          });
        }
      });

      // Add midpoint targets for segments
      path.segments.forEach((segment, index) => {
        const fromNode = path.nodes[segment.from];
        const toNode = path.nodes[segment.to];
        
        if (fromNode && toNode) {
          const midpoint = vec2.lerp(fromNode.p, toNode.p, 0.5);
          this.objectTargets.push({
            id: `${path.id}_midpoint_${index}`,
            type: 'midpoint',
            point: midpoint,
            priority: 8,
            metadata: { pathId: path.id, segmentIndex: index }
          });
        }
      });
    });
  }

  addGuides(guides) {
    this.guideTargets = guides.map((point, index) => ({
      id: `guide_${index}`,
      type: 'guide',
      point,
      priority: 15,
      metadata: { guideIndex: index }
    }));
  }

  snap(point, excludeTypes = []) {
    if (!this.config.enabled) {
      return { snapped: false, point, distance: 0 };
    }

    // Simple distance-based snapping for now
    const candidates = [...this.objectTargets, ...this.guideTargets];
    let bestTarget;
    let bestDistance = this.config.tolerance;

    for (const target of candidates) {
      if (excludeTypes.includes(target.type)) continue;

      const distance = vec2.distance(point, target.point);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestTarget = target;
      } else if (distance === bestDistance && target.priority > (bestTarget?.priority || 0)) {
        bestTarget = target;
      }
    }

    if (bestTarget) {
      return {
        snapped: true,
        point: bestTarget.point,
        target: bestTarget,
        distance: bestDistance
      };
    }

    return { snapped: false, point, distance: 0 };
  }

  snapWithAngle(fromPoint, toPoint) {
    let result = this.snap(toPoint);
    
    if (this.config.angles && !result.snapped) {
      const angleSnapped = snapAngle(fromPoint, toPoint, this.config.angleStep);
      const distance = vec2.distance(toPoint, angleSnapped);
      
      if (distance <= this.config.tolerance) {
        result = {
          snapped: true,
          point: angleSnapped,
          target: {
            id: 'angle_snap',
            type: 'angle',
            point: angleSnapped,
            priority: 12,
            metadata: { angle: vec2.angle(vec2.subtract(angleSnapped, fromPoint)) }
          },
          distance
        };
      }
    }

    return result;
  }

  snapOrthogonal(fromPoint, toPoint) {
    const orthoPoint = snapToOrthogonal(fromPoint, toPoint);
    const distance = vec2.distance(toPoint, orthoPoint);
    
    if (distance <= this.config.tolerance) {
      return {
        snapped: true,
        point: orthoPoint,
        target: {
          id: 'ortho_snap',
          type: 'angle',
          point: orthoPoint,
          priority: 11,
          metadata: { orthogonal: true }
        },
        distance
      };
    }

    return { snapped: false, point: toPoint, distance: 0 };
  }
}

// ============================================================================
// Snap Visualization
// ============================================================================

export function createSnapIndicators(snapResult) {
  if (!snapResult.snapped || !snapResult.target) return [];

  const indicators = [];

  switch (snapResult.target.type) {
    case 'grid':
      indicators.push({
        type: 'point',
        position: snapResult.point,
        color: '#00ff00',
        size: 4,
        metadata: { snapType: 'grid' }
      });
      break;

    case 'endpoint':
      indicators.push({
        type: 'point',
        position: snapResult.point,
        color: '#ff0000',
        size: 6,
        metadata: { snapType: 'endpoint' }
      });
      break;

    case 'midpoint':
      indicators.push({
        type: 'point',
        position: snapResult.point,
        color: '#0000ff',
        size: 5,
        metadata: { snapType: 'midpoint' }
      });
      break;

    case 'angle':
      indicators.push({
        type: 'point',
        position: snapResult.point,
        color: '#ff8800',
        size: 5,
        metadata: { snapType: 'angle' }
      });
      break;

    case 'guide':
      indicators.push({
        type: 'point',
        position: snapResult.point,
        color: '#8800ff',
        size: 5,
        metadata: { snapType: 'guide' }
      });
      break;
  }

  return indicators;
}
